<template>
    <label
        ref="labelRef"
        class="fontawesome-radio"
        :class="{ 'is-disabled': disabled }"
        @click="emit('click')"
        @keydown.space.prevent="handleSpace"
        tabindex="0"
    >
        <input type="radio" v-model="internalValue" :disabled="disabled" :name="name" :value="initialValue" tabindex="-1" />
        <span>
            <slot></slot>
        </span>
    </label>
</template>

<script setup lang="ts">
    import { computed, ref } from "vue";

    /**
     * If you struggle with setting the default value and having preselected radio,
     * set the type to NULL | Type and in main script setup set v-model to null and then in openModal() set it to default value.
     *
     * The issue isn't about timing - it's about detecting a change. If the value never changes (starts as "selection" and remains "selection"), Vue might not properly sync the native input's checked state. But if the value changes from null to "selection", Vue's reactivity system kicks in and updates everything correctly.
     */

    const props = defineProps<{
        disabled?: boolean;
        modelValue: string | number | undefined;
        name: string;
        initialValue: string | number;
    }>();

    const emit = defineEmits<{
        click: [];
        "update:modelValue": [value: string | number];
    }>();

    const labelRef = ref<HTMLLabelElement>();

    const internalValue = computed({
        get() {
            return props.modelValue;
        },
        set(newValue: string | number) {
            emit("update:modelValue", newValue);
        },
    });

    function handleSpace() {
        if (!props.disabled) {
            // Always emit the value to ensure the radio is selected
            emit("update:modelValue", props.initialValue);
            // Also trigger click on the input to ensure proper radio behavior
            const input = labelRef.value?.querySelector('input[type="radio"]') as HTMLInputElement | null;
            if (input) {
                input.click();
            }
        }
    }
</script>
